import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { IoCaretDownSharp, IoCaretUpSharp, IoInformationCircle } from 'react-icons/io5'
import { RiArrowDownSLine } from 'react-icons/ri'
import Seo from './Seo'
import { assetUrl, axiosForAPI, axiosForContent, formatChampionDisplayName } from '../helper_functions/utilities'
import { PLATFORM_OPTIONS } from '../helper_functions/searchRouting'
import { scopedPlatformOptions, useDeploymentCapabilities } from '../context/DeploymentCapabilitiesContext'
import { AssetImage } from './match/MatchSharedElements'
import ChampionAbilityIconWithFallBack from '../shared/ChampionAbilityIcons'

const ROLE_OPTIONS = [
    { value: 'TOP', label: 'TOP', icon: `${process.env.PUBLIC_URL}/assets/role_icons/position-top-light.svg` },
    { value: 'JUNGLE', label: 'JUNGLE', icon: `${process.env.PUBLIC_URL}/assets/role_icons/position-jungle-light.svg` },
    { value: 'MIDDLE', label: 'MID', icon: `${process.env.PUBLIC_URL}/assets/role_icons/position-middle-light.svg` },
    { value: 'BOTTOM', label: 'BOTTOM', icon: `${process.env.PUBLIC_URL}/assets/role_icons/position-bottom-light.svg` },
    { value: 'UTILITY', label: 'SUPPORT', icon: `${process.env.PUBLIC_URL}/assets/role_icons/position-utility-light.svg` },
]

const ROLE_PATH_TO_ID = {
    top: 'TOP',
    jungle: 'JUNGLE',
    mid: 'MIDDLE',
    bot: 'BOTTOM',
    support: 'UTILITY',
}

const SEO_TIER_PATH_TO_VALUE = {
    challenger: 'Challenger',
}

function parseAnalyticsScope(scopePath) {
    const segments = String(scopePath || '').split('/').filter(Boolean).map((segment) => segment.toLowerCase())
    const tierSegment = segments.find((segment) => SEO_TIER_PATH_TO_VALUE[segment])
    return {
        tier: tierSegment ? SEO_TIER_PATH_TO_VALUE[tierSegment] : '',
        // The backend may add a configured platform without a frontend release.
        // Scope suffixes are intentionally platform-code shaped, so accept the
        // non-tier segment and let the API validate the configured value.
        platform: segments.find((segment) => segment !== tierSegment) || '',
    }
}

function analyticsScopeSuffix({ tier, platform }) {
    const segments = []
    if (tier === 'Challenger') segments.push('challenger')
    if (platform) segments.push(platform)
    return segments.length ? `/${segments.join('/')}` : ''
}

function roleIdFromPath(rolePath) {
    return ROLE_PATH_TO_ID[String(rolePath || '').toLowerCase()] || ''
}

const VIEW_OPTIONS = [
    { value: 'lane', label: 'Lane profile' },
    { value: 'duration', label: 'Game duration' },
    { value: 'side', label: 'Map side' },
    { value: 'starting-items', label: 'Starting items' },
    { value: 'boots', label: 'Completed boots' },
    { value: 'item-builds', label: 'Item builds' },
    { value: 'rune-pages', label: 'Rune pages' },
    { value: 'spells', label: 'Summoner spells' },
    { value: 'first-three-skills', label: 'First three skills' },
    { value: 'primary-skill', label: 'First spell maxed' },
    { value: 'evolution-paths', label: 'Evolution paths' },
    { value: 'matchup', label: 'Matchups' },
    { value: 'synergy', label: 'Synergy' },
]

const REPORT_OPTIONS = VIEW_OPTIONS.filter((option) => !['lane', 'side', 'duration', 'starting-items', 'spells', 'first-three-skills', 'primary-skill', 'evolution-paths'].includes(option.value))

const MASTERY_OPTIONS = ['', '0-4', '5-8', '9-13', '14-21', '22+']
const NO_OBSERVATIONS_MESSAGE = 'No observations.'
const MASTERY_LABELS = {
    '0-4': 'LOW',
    '5-8': 'MID-LOW',
    '9-13': 'MID',
    '14-21': 'MID-HIGH',
    '22+': 'HIGH',
}
const MASTERY_BUCKET_TO_URL = {
    '0-4': 'low',
    '5-8': 'mid-low',
    '9-13': 'mid',
    '14-21': 'mid-high',
    '22+': 'high',
}
const MASTERY_URL_TO_BUCKET = Object.fromEntries(Object.entries(MASTERY_BUCKET_TO_URL).map(([bucket, urlValue]) => [urlValue, bucket]))

function masteryBucketFromUrl(value) {
    const rawValue = String(value || '').trim().toLowerCase()
    return MASTERY_URL_TO_BUCKET[rawValue] || (MASTERY_OPTIONS.includes(value) ? value : '')
}

function masteryUrlValue(bucket) {
    return MASTERY_BUCKET_TO_URL[bucket] || bucket
}

const TIER_OPTIONS = [
    { value: 'Emerald+', label: 'EMERALD+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Emerald.png` },
    { value: 'Diamond+', label: 'DIAMOND+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Diamond.png` },
    { value: 'Master+', label: 'MASTER+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Master.png` },
    { value: 'Grandmaster+', label: 'GRANDMASTER+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Grandmaster.png` },
    { value: 'Iron', label: 'IRON', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Iron.png` },
    { value: 'Bronze', label: 'BRONZE', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Bronze.png` },
    { value: 'Silver', label: 'SILVER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Silver.png` },
    { value: 'Gold', label: 'GOLD', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Gold.png` },
    { value: 'Platinum', label: 'PLATINUM', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Platinum.png` },
    { value: 'Emerald', label: 'EMERALD', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Emerald.png` },
    { value: 'Diamond', label: 'DIAMOND', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Diamond.png` },
    { value: 'Master', label: 'MASTER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Master.png` },
    { value: 'Grandmaster', label: 'GRANDMASTER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Grandmaster.png` },
    { value: 'Challenger', label: 'CHALLENGER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Challenger.png` },
]

function FilterSelect({ ariaLabel, icon, options, value, onChange, disabled = false, highlight = false }) {
    return (
        <div className="relative">
            {icon ? <img alt="" aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 object-contain" src={icon} /> : null}
            <select
                aria-label={ariaLabel}
                className={`w-full appearance-none rounded-md border py-2 pr-7 text-xs transition-colors focus:outline-none disabled:cursor-not-allowed disabled:border-zinc-800 disabled:bg-zinc-950 disabled:text-zinc-600 disabled:opacity-70 ${highlight ? 'border-cyan-700/80 bg-cyan-950/35 text-cyan-100 shadow-[inset_0_1px_0_rgba(103,232,249,0.08),0_0_0_1px_rgba(34,211,238,0.08)] focus:border-cyan-400/80 focus:ring-1 focus:ring-cyan-400/45' : 'border-zinc-700 bg-zinc-900 text-zinc-200 focus:border-cyan-700/70 focus:ring-1 focus:ring-cyan-700/45'} ${icon ? 'pl-9' : 'px-2'}`}
                disabled={disabled}
                value={value}
                onChange={onChange}
            >
                {options.map((option) => <option key={option.value || 'global'} value={option.value} disabled={option.disabled}>{option.label}</option>)}
            </select>
            <RiArrowDownSLine aria-hidden="true" className={`pointer-events-none absolute right-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 ${highlight ? 'text-cyan-300' : 'text-zinc-500'}`} />
        </div>
    )
}

function MetricLabel({ label, description }) {
    return (
        <div className="flex min-h-[18px] items-start justify-center gap-1 text-[10px] font-bold uppercase leading-tight tracking-[0.16em] text-zinc-500">
            <span>{label}</span>
            <span className="group relative -mt-0.5 inline-flex shrink-0">
                <button
                    aria-label={`${label} information`}
                    className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-zinc-500 transition-colors hover:text-cyan-300 focus:outline-none focus-visible:text-cyan-300"
                    type="button"
                >
                    <IoInformationCircle aria-hidden="true" className="h-3.5 w-3.5" />
                </button>
                <span className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 w-48 -translate-x-1/2 rounded-md border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-left text-[10px] font-medium normal-case leading-snug tracking-normal text-zinc-300 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" role="tooltip">
                    {description}
                </span>
            </span>
        </div>
    )
}

function percent(value) {
    return value === null || value === undefined ? '-' : `${Number(value).toFixed(1)}%`
}

function compactPercent(value) {
    return percent(value).replace('.0%', '%')
}

function compactGames(value) {
    const numericValue = Number(value || 0)
    return `${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(numericValue)} Games`
}

function compactSpellGames(value) {
    const numericValue = Math.max(0, Number(value || 0))
    if (numericValue < 1000) return `${Math.round(numericValue)} games`

    const units = [
        { threshold: 1_000_000_000, suffix: 'b' },
        { threshold: 1_000_000, suffix: 'm' },
        { threshold: 1_000, suffix: 'k' },
    ]
    const unit = units.find(({ threshold }) => numericValue >= threshold)
    if (!unit) return `${Math.round(numericValue)} games`

    return `${(numericValue / unit.threshold).toFixed(1)}${unit.suffix} games`
}

function compactRelationshipGames(value) {
    const numericValue = Math.max(0, Number(value || 0))
    if (numericValue < 100_000) return `${Math.round(numericValue).toLocaleString('en-US')} games`
    if (numericValue < 1_000_000) return `${(numericValue / 1_000).toFixed(1)}k games`
    return `${(numericValue / 1_000_000).toFixed(1)}m games`
}

function winRateColor(value) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 'rgb(161 161 170)'
    const distance = Math.min(1, Math.abs(numericValue - 50) / 8)
    const target = numericValue >= 50 ? [103, 232, 249] : [253, 230, 138]
    const neutral = [148, 163, 184]
    const color = neutral.map((channel, index) => Math.round(channel + ((target[index] - channel) * distance)))
    return `rgb(${color.join(' ')})`
}

function winRateDeltaColor(value) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 'rgb(161 161 170)'
    const intensity = Math.min(1, Math.abs(numericValue) / 5)
    const target = numericValue >= 0 ? [103, 232, 249] : [253, 230, 138]
    const neutral = [148, 163, 184]
    const color = neutral.map((channel, index) => Math.round(channel + ((target[index] - channel) * intensity)))
    return `rgb(${color.join(' ')})`
}

function RateText({ value }) {
    return <span style={{ color: winRateColor(value) }}>{percent(value)}</span>
}

function rowPickRate(row, totalPicks) {
    if (row?.pick_rate !== null && row?.pick_rate !== undefined) return row.pick_rate
    const observedGames = row?.picked ?? row?.games
    if (!totalPicks || observedGames === null || observedGames === undefined) return null
    return (Number(observedGames) / Number(totalPicks)) * 100
}

function PickWinRates({ pickRate, winRate }) {
    return (
        <span aria-label={`Pick rate ${compactPercent(pickRate)}, win rate ${compactPercent(winRate)}`} className="inline-flex flex-col items-start gap-0 text-[0.9em] leading-tight tabular-nums" title="Pick rate / win rate">
            <span><span className="text-zinc-500">P:</span> <span className="text-cyan-300">{compactPercent(pickRate)}</span></span>
            <span><span className="text-zinc-500">W:</span> <span style={{ color: winRateColor(winRate) }}>{compactPercent(winRate)}</span></span>
        </span>
    )
}

function banRateColor(value) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 'rgb(161 161 170)'
    if (numericValue < 5) return 'rgb(103 232 249)'

    const distance = Math.min(1, Math.max(0, (numericValue - 5) / 25))
    const neutral = [148, 163, 184]
    const amber = [253, 230, 138]
    const color = neutral.map((channel, index) => Math.round(channel + ((amber[index] - channel) * distance)))
    return `rgb(${color.join(' ')})`
}

function BanRateText({ value }) {
    return <span style={{ color: banRateColor(value) }}>{percent(value)}</span>
}

function signed(value) {
    if (value === null || value === undefined) return '-'
    const rounded = Number(value).toFixed(1)
    return Number(value) > 0 ? `+${rounded}` : rounded
}

function signedInteger(value) {
    if (value === null || value === undefined) return '-'
    const rounded = Math.round(Number(value))
    return rounded > 0 ? `+${rounded}` : String(rounded)
}

function parseLockedItems(value) {
    if (!value) return []
    return value.split(',').flatMap((token) => {
        const [rawSlot, rawItem] = token.split(':')
        const slot = Number(rawSlot)
        const itemId = Number(rawItem)
        return Number.isInteger(slot) && slot >= 1 && slot <= 6 && itemId > 0
            ? [{ slot, itemId }]
            : []
    })
}

function parseLockedEvolutions(value) {
    if (!value) return []
    return value.split(',').flatMap((token) => {
        const [rawSlot, rawAbility] = token.split(':')
        const slot = Number(rawSlot)
        const ability = String(rawAbility || '').toLowerCase()
        return Number.isInteger(slot) && slot >= 1 && slot <= 4 && ['q', 'w', 'e', 'r'].includes(ability)
            ? [{ slot, evolutionSlot: ability }]
            : []
    })
}

function ItemToken({ id, name, quantity = 1 }) {
    if (Number(id) === -1) {
        return <span className="rounded border border-zinc-700 px-2 py-1 text-[10px] text-zinc-400" title="No purchase">NO PURCHASE</span>
    }

    return (
        <span className="inline-flex items-center gap-1" title={name || `Item ${id}`}>
            <img className="h-7 w-7 rounded border border-zinc-700 bg-zinc-900 object-cover" src={assetUrl(`/item_icons/${id}.png`)} alt={name || `Item ${id}`} />
            {quantity > 1 ? <span className="text-[10px] text-zinc-400">x{quantity}</span> : null}
        </span>
    )
}

function SpellToken({ id, name }) {
    return <img className="h-7 w-7 rounded border border-zinc-700 bg-zinc-900 object-cover" src={assetUrl(`/summoner_spell_icons/${id}.png`)} alt={name || `Summoner spell ${id}`} title={name || `Summoner spell ${id}`} />
}

function RuneToken({ id, name, runeAssets, prominent = false }) {
    const src = runeAssets?.[String(id)]
    return (
        <span title={name || `Rune ${id}`}>
            <AssetImage
                alt={name || `Rune ${id}`}
                className={`${prominent ? 'h-9 w-9' : 'h-7 w-7'} rounded-full border border-cyan-800/40 bg-zinc-900 object-cover`}
                src={src}
            />
        </span>
    )
}

function RunePageVisual({ row, runeAssets }) {
    return (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex items-center gap-1.5" title="Keystone">
                <RuneToken id={row.keystone_id} name={row.keystone_name} runeAssets={runeAssets} prominent />
                <div className="flex gap-1">
                    {(row.primary_perks || []).map((id, index) => <RuneToken key={id} id={id} name={row.primary_perk_names?.[index]} runeAssets={runeAssets} />)}
                </div>
            </div>
            <span className="text-zinc-700">/</span>
            <div className="flex gap-1" title="Secondary runes">
                {(row.secondary_perks || []).map((id, index) => <RuneToken key={id} id={id} name={row.secondary_perk_names?.[index]} runeAssets={runeAssets} />)}
            </div>
            <span className="text-zinc-700">/</span>
            <div className="flex gap-1" title="Stat shards">
                {(row.shards || []).map((id, index) => <RuneToken key={id} id={id} name={row.shard_names?.[index]} runeAssets={runeAssets} />)}
            </div>
        </div>
    )
}

function KeystonePerformance({ rows, loading, runeAssets, selectedKeystoneId, onSelect, totalPicks }) {
    return (
        <section className="mt-3 rounded-xl border border-zinc-800 bg-zinc-950/75 p-3">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Keystone performance</div>
                    <div className="mt-1 text-xs text-zinc-400">Select a Keystone to filter on complete Rune Pages stats.</div>
                </div>
                {selectedKeystoneId ? <button className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-400 hover:text-cyan-200" onClick={() => onSelect('')} type="button">Show all</button> : null}
            </div>
            {loading ? <div className="py-3 text-xs text-zinc-500">Loading keystone data...</div> : rows.length ? (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {rows.map((row) => {
                        const active = String(row.keystone_id) === String(selectedKeystoneId)
                        return (
                            <button
                                className={`flex items-center gap-3 rounded-lg border px-2.5 py-2 text-left transition ${active ? 'border-cyan-500/70 bg-cyan-950/30' : 'border-zinc-800/80 bg-zinc-950/80 hover:border-zinc-600 hover:bg-zinc-900'}`}
                                key={row.keystone_id}
                                onClick={() => onSelect(active ? '' : String(row.keystone_id))}
                                type="button"
                            >
                                <RuneToken id={row.keystone_id} name={row.keystone_name} runeAssets={runeAssets} prominent />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-xs font-semibold text-zinc-200">{row.keystone_name || `Keystone ${row.keystone_id}`}</span>
                                    <span className="mt-0.5 block text-[10px] tabular-nums text-zinc-500">{compactGames(row.picked)}</span>
                                </span>
                                <span className="text-xs font-semibold"><PickWinRates pickRate={rowPickRate(row, totalPicks)} winRate={row.win_rate} /></span>
                            </button>
                        )
                    })}
                </div>
            ) : <div className="py-3 text-xs text-zinc-600">No observations.</div>}
        </section>
    )
}

const DURATION_ORDER = ['lt20', '20-25', '25-30', '30-35', '35-40', '40+']
const DURATION_LABELS = { lt20: '<20', '20-25': '20-25', '25-30': '25-30', '30-35': '30-35', '35-40': '35-40', '40+': '40+' }
function averageMetric(value, digits = 1) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-'
    return Number(value).toLocaleString('en-US', {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    })
}

function minutesAsClock(value) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-'
    const totalSeconds = Math.round(Number(value) * 60)
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`
}

const LANE_CHECKPOINTS = [5, 10, 15, 20, 25, 30]
const LANE_METRICS = [
    ['Gold', 'gold'],
    ['CS', 'cs'],
    ['Level', 'level'],
    ['KP', 'kill_participation'],
]

function PerformanceGroup({ accent = false, title, metrics, children }) {
    const metricGridClassName = metrics.length >= 5
        ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
        : metrics.length === 4
            ? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-5'
            : metrics.length === 3
                ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
                : 'grid-cols-2 lg:grid-cols-5'

    return (
        <section className={accent ? 'rounded-lg border border-cyan-800/60 bg-cyan-950/10 p-3' : ''}>
            <div className="mb-3 flex items-center gap-3">
                <h3 className={`shrink-0 text-[11px] font-bold uppercase tracking-[0.16em] ${accent ? 'text-cyan-200' : 'text-zinc-300'}`}>{title}</h3>
                <div className={`h-px flex-1 ${accent ? 'bg-cyan-800/50' : 'bg-zinc-800/80'}`} />
            </div>
            <div className={`grid gap-x-5 gap-y-4 ${metricGridClassName}`}>
                {metrics.map(([label, value, description, progress]) => (
                    <div className="min-w-0" key={label} title={description}>
                        <div className="truncate text-[11px] font-medium text-zinc-500">{label}</div>
                        <div className={`mt-0.5 text-[24px] font-semibold leading-none tabular-nums tracking-tight ${accent ? 'text-cyan-100' : 'text-zinc-100'}`}>{value}</div>
                        {progress !== undefined ? (
                            <div className="mt-2 h-1 overflow-hidden rounded-full bg-zinc-800">
                                <div className="h-full rounded-full bg-cyan-400" style={{ width: `${Math.max(0, Math.min(100, Number(progress) || 0))}%` }} />
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
            {children}
        </section>
    )
}

function ChampionPerformanceSummary({ performance, roleId, championId, jungleItemCompletion, supportItemCompletion, transformations, loading }) {
    const [isExpanded, setIsExpanded] = useState(false)
    const performanceStorageKey = championId ? `wrgg:champion-performance-expanded:${championId}:${roleId || 'all'}` : null
    const jungleItemCompletionTime = jungleItemCompletion?.average_completion_time_minutes
    const benchmarkMetrics = [
        ['First legendary time', minutesAsClock(performance?.average_first_legendary_time_minutes)],
    ]
    if (roleId === 'JUNGLE') benchmarkMetrics.push(['Jungle item completion time', minutesAsClock(jungleItemCompletionTime)])
    if (roleId === 'UTILITY') benchmarkMetrics.push(['Support item completion time', minutesAsClock(supportItemCompletion?.average_transformation_time_minutes)])
    const transformation = transformations.find((candidate) => Number(candidate.form_champion_id) === Number(championId))
    if (transformation) benchmarkMetrics.push(['Transformation time', minutesAsClock(transformation.average_transformation_time_minutes), `${compactGames(transformation.games)} observed`])

    useEffect(() => {
        if (!performanceStorageKey) {
            setIsExpanded(false)
            return
        }

        try {
            setIsExpanded(window.localStorage.getItem(performanceStorageKey) === '1')
        } catch {
            setIsExpanded(false)
        }
    }, [performanceStorageKey])

    function toggleExpanded() {
        setIsExpanded((current) => {
            const next = !current
            if (performanceStorageKey) {
                try {
                    if (next) window.localStorage.setItem(performanceStorageKey, '1')
                    else window.localStorage.removeItem(performanceStorageKey)
                } catch {
                    // Keep the control usable when storage is unavailable.
                }
            }
            return next
        })
    }

    return (
        <section className="rounded-xl border border-zinc-800 bg-zinc-950/75 p-3">
            <button
                aria-expanded={isExpanded}
                className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${isExpanded ? 'border-zinc-800 bg-zinc-900/70 hover:border-zinc-700 hover:bg-zinc-900' : 'border-cyan-800/60 bg-cyan-950/10 hover:border-cyan-700/80 hover:bg-cyan-950/20'}`}
                onClick={toggleExpanded}
                type="button"
            >
                <span className={`text-[11px] font-bold uppercase tracking-[0.16em] ${isExpanded ? 'text-zinc-300' : 'text-cyan-200'}`}>CHAMPION PERFORMANCE</span>
                {isExpanded ? <IoCaretUpSharp className="h-4 w-4 shrink-0 text-zinc-400" /> : <IoCaretDownSharp className="h-4 w-4 shrink-0 text-cyan-300" />}
            </button>
            {isExpanded ? (loading ? <div className="mt-4 h-32 animate-pulse rounded-md bg-zinc-900/80" /> : (
                <div className="mt-4 space-y-5">
                    <PerformanceGroup title="Combat" metrics={[
                        ['Damage share', percent(performance?.average_damage_share), 'Average share of your team\'s champion damage dealt by this champion.'],
                        ['Kills', averageMetric(performance?.average_kills)],
                        ['Deaths', averageMetric(performance?.average_deaths)],
                        ['Assists', averageMetric(performance?.average_assists)],
                        ['First blood participation', percent(performance?.first_blood_participation_rate), 'Percentage of games where this champion was the first-blood killer or an assisting participant.', performance?.first_blood_participation_rate],
                    ]} />
                    <PerformanceGroup title="Laning" metrics={[
                        ['CS / min', averageMetric(performance?.average_csm), 'Average total minion and neutral-monster CS per minute.'],
                        ['Turret plates', averageMetric(performance?.average_turret_plates)],
                        ['Turret takedowns', averageMetric(performance?.average_turret_takedowns)],
                    ]} />
                    <PerformanceGroup title="Team play" metrics={[
                        ['Epic objectives', percent(performance?.epic_objective_participation_rate), undefined, performance?.epic_objective_participation_rate],
                        ['Wards placed', averageMetric(performance?.average_wards_placed)],
                        ['Wards destroyed', averageMetric(performance?.average_wards_killed)],
                        ['Surrender rate', percent(performance?.surrender_rate), 'Percentage of games surrendered when this champion was on the surrendering team. Riot does not provide individual vote or initiation data.', performance?.surrender_rate],
                    ]} />
                    <PerformanceGroup accent title="Benchmarks" metrics={benchmarkMetrics} />
                </div>
            )) : null}
        </section>
    )
}

function LaneProfileSummary({ rows, loading }) {
    const rowsByCheckpoint = new Map(rows.map((row) => [Number(row?.checkpoint_minute), row]))
    const checkpointRows = LANE_CHECKPOINTS.map((minute) => ({
        minute,
        row: rowsByCheckpoint.get(minute),
    }))

    function metricValues(metric) {
        return checkpointRows.map(({ row }) => {
            const value = row?.[`${metric}_diff_average`]
            if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) {
                return null
            }
            const numericValue = Number(value)
            return metric === 'kill_participation' ? Math.round(numericValue) : numericValue
        })
    }

    function chartDomain(values) {
        const validValues = values.filter((value) => value !== null)
        return {
            min: Math.min(0, ...(validValues.length ? validValues : [0])),
            max: Math.max(0, ...(validValues.length ? validValues : [0])),
        }
    }

    function DivergingBars({ label, metric }) {
        const values = metricValues(metric)
        const { min, max } = chartDomain(values)
        // Compress late-game outliers while preserving direction and the exact displayed value.
        const visualMax = Math.max(Math.sqrt(Math.abs(min)), Math.sqrt(Math.abs(max)), 1)
        const chart = { left: 18, right: 242, zeroY: 78 }
        const barWidth = 24
        const xStep = (chart.right - chart.left) / values.length
        const barHeight = (value) => Math.max(3, (Math.sqrt(Math.abs(value)) / visualMax) * 48)
        const formatLead = (value) => {
            if (metric === 'kill_participation') return `${signedInteger(value)} pp`
            return metric === 'gold' ? signedInteger(value) : signed(value)
        }
        const leadColor = (value) => value >= 0 ? 'rgb(103 232 249)' : 'rgb(253 230 138)'
        return (
            <div className="rounded-md border border-zinc-800/80 bg-zinc-950/90 px-2 py-2">
                <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400">{label}</div>
                    <div className="text-[10px] tabular-nums text-zinc-600">ahead / behind</div>
                </div>
                <svg aria-label={`${label} role lead by checkpoint`} className="h-40 w-full" role="img" viewBox="0 0 260 176">
                    <line stroke="rgb(113 113 122)" strokeWidth="1" x1={chart.left} x2={chart.right} y1={chart.zeroY} y2={chart.zeroY} />
                    {values.map((value, index) => {
                        const x = chart.left + (index * xStep) + ((xStep - barWidth) / 2)
                        const height = value === null ? 0 : barHeight(value)
                        const y = value !== null && value >= 0 ? chart.zeroY - height : chart.zeroY
                        const color = value !== null ? leadColor(value) : 'rgb(82 82 91)'
                        return (
                            <g key={`${metric}-${index}`}>
                                {value !== null ? (
                                    <>
                                        <rect fill={color} height={height} rx="3" width={barWidth} x={x} y={y} />
                                        <text fill={color} fontSize="11" fontWeight="600" textAnchor="middle" x={x + (barWidth / 2)} y={value >= 0 ? y - 7 : y + height + 15}>{formatLead(value)}</text>
                                        <title>{metric === 'kill_participation'
                                            ? `${checkpointRows[index].minute}:00: ${formatLead(value)} (champion ${percent(checkpointRows[index].row?.kill_participation_average)}, same role ${percent(checkpointRows[index].row?.opponent_kill_participation_average)})`
                                            : `${checkpointRows[index].minute}:00: ${formatLead(value)}`}</title>
                                    </>
                                ) : <text fill="rgb(82 82 91)" fontSize="11" textAnchor="middle" x={x + (barWidth / 2)} y={chart.zeroY + 4}>-</text>}
                                <text fill="rgb(161 161 170)" fontSize="11" fontWeight="600" textAnchor="middle" x={x + (barWidth / 2)} y="158">{checkpointRows[index].minute}m</text>
                            </g>
                        )
                    })}
                </svg>
            </div>
        )
    }

    return (
        <section className="rounded-xl border border-zinc-800 bg-zinc-950/75 p-2.5">
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Average role lead / Scaling Profile</div>
            {loading ? <div className="h-40 animate-pulse rounded-md bg-zinc-900/80" /> : (
                <div className="grid gap-2 md:grid-cols-4">
                    {LANE_METRICS.map(([label, metric]) => <DivergingBars key={metric} label={label} metric={metric} />)}
                </div>
            )}
        </section>
    )
}

function DurationChart({ rows }) {
    const orderedRows = DURATION_ORDER
        .map((bucket) => rows.find((row) => row.duration_bucket === bucket))
    const chart = { left: 42, right: 704, top: 14, bottom: 144 }
    const xStep = orderedRows.length > 1 ? (chart.right - chart.left) / (orderedRows.length - 1) : 0
    const points = orderedRows.map((row, index) => ({
        row,
        x: chart.left + (index * xStep),
        y: row ? chart.bottom - ((Number(row.win_rate || 0) / 100) * (chart.bottom - chart.top)) : null,
    }))
    const plottedPoints = points.filter((point) => point.row)
    const path = plottedPoints.length > 1
        ? plottedPoints.slice(1).reduce((result, point, index) => {
            const previous = plottedPoints[index]
            const midpoint = (point.x - previous.x) / 2
            return `${result} C ${previous.x + midpoint} ${previous.y}, ${point.x - midpoint} ${point.y}, ${point.x} ${point.y}`
        }, `M ${plottedPoints[0].x} ${plottedPoints[0].y}`)
        : plottedPoints.length === 1 ? `M ${plottedPoints[0].x} ${plottedPoints[0].y}` : ''

    return (
        <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/80 px-2 pt-3 pb-2 sm:px-4">
            <div className="mb-2 px-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Win rate</div>
            </div>
            <svg aria-label="Win rate by game duration" className="h-auto w-full" role="img" viewBox="0 0 740 190">
                {[0, 25, 50, 75, 100].map((value) => {
                    const y = chart.bottom - ((value / 100) * (chart.bottom - chart.top))
                    return (
                        <g key={value}>
                            <line stroke="rgba(63, 63, 70, 0.55)" strokeDasharray="2 5" x1={chart.left} x2={chart.right} y1={y} y2={y} />
                            <text fill="rgb(161 161 170)" fontSize="12" fontWeight="600" textAnchor="end" x="26" y={y + 4}>{value}%</text>
                        </g>
                    )
                })}
                {path ? <path d={path} fill="none" stroke="rgb(34 211 238)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" /> : null}
                {points.map(({ row, x, y }, index) => (
                    <g key={DURATION_ORDER[index]}>
                        {row ? <><circle cx={x} cy={y} fill="rgb(9 14 22)" r="5" stroke="rgb(103 232 249)" strokeWidth="2" /><title>{`${DURATION_LABELS[row.duration_bucket]}: ${percent(row.win_rate)} across ${row.games} games`}</title></> : null}
                        <text fill={row ? 'rgb(161 161 170)' : 'rgb(82 82 91)'} fontSize="12" fontWeight="600" textAnchor="middle" x={x} y="168">{DURATION_LABELS[DURATION_ORDER[index]]}</text>
                    </g>
                ))}
            </svg>
        </div>
    )
}

function trendPoints(rows, metric) {
    const chart = { left: 42, right: 704, top: 14, bottom: 144 }
    return rows.map((row, index) => ({
        row,
        x: rows.length > 1 ? chart.left + ((index / (rows.length - 1)) * (chart.right - chart.left)) : (chart.left + chart.right) / 2,
        value: row[metric] === null || row[metric] === undefined ? null : Number(row[metric]),
        y: row[metric] === null || row[metric] === undefined
            ? null
            : chart.bottom - ((Math.min(100, Math.max(0, Number(row[metric]))) / 100) * (chart.bottom - chart.top)),
    }))
}

function trendPath(points) {
    const plottedPoints = points.filter((point) => point.y !== null)
    if (plottedPoints.length < 2) return plottedPoints.length === 1 ? `M ${plottedPoints[0].x} ${plottedPoints[0].y}` : ''
    return plottedPoints.slice(1).reduce((result, point, index) => {
        const previous = plottedPoints[index]
        const midpoint = (point.x - previous.x) / 2
        return `${result} C ${previous.x + midpoint} ${previous.y}, ${point.x - midpoint} ${point.y}, ${point.x} ${point.y}`
    }, `M ${plottedPoints[0].x} ${plottedPoints[0].y}`)
}

function RateTrendChart({ rows }) {
    const chart = { left: 30, right: 692, top: 14, bottom: 144 }
    const patchLabelY = 180
    const pickPoints = trendPoints(rows, 'pick_rate')
    const banPoints = trendPoints(rows, 'ban_rate')

    return (
        <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/80 px-2 pt-3 pb-2 sm:px-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Pick rate / ban rate</div>
                <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.12em]">
                    <span className="flex items-center gap-1.5 text-cyan-300"><span className="h-2 w-2 rounded-full bg-cyan-300" />Pick rate</span>
                    <span className="flex items-center gap-1.5 text-amber-200"><span className="h-2 w-2 rounded-full bg-amber-200" />Ban rate</span>
                </div>
            </div>
            <svg aria-label="Pick rate and ban rate by patch" className="h-auto w-full" role="img" viewBox="0 0 740 190">
                {[0, 25, 50, 75, 100].map((value) => {
                    const y = chart.bottom - ((value / 100) * (chart.bottom - chart.top))
                    return (
                        <g key={value}>
                            <line stroke="rgba(63, 63, 70, 0.55)" strokeDasharray="2 5" x1={chart.left} x2={chart.right} y1={y} y2={y} />
                            <text fill="rgb(161 161 170)" fontSize="12" fontWeight="600" textAnchor="end" x="34" y={y + 4}>{value}%</text>
                        </g>
                    )
                })}
                {trendPath(banPoints) ? <path d={trendPath(banPoints)} fill="none" stroke="rgb(253 230 138)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" /> : null}
                {banPoints.map(({ row, x, y, value }) => (
                    <g key={row.patch}>
                        {y !== null ? <><circle cx={x} cy={y} fill="rgb(9 14 22)" r="4" stroke="rgb(253 230 138)" strokeWidth="2" /><title>{`${row.patch}: ${percent(value)} ban rate across ${Number(row.games || 0).toLocaleString()} games`}</title></> : null}
                    </g>
                ))}
                {trendPath(pickPoints) ? <path d={trendPath(pickPoints)} fill="none" stroke="rgb(103 232 249)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" /> : null}
                {pickPoints.map(({ row, x, y, value }) => (
                    <g key={`pick-${row.patch}`}>
                        {y !== null ? <><circle cx={x} cy={y} fill="rgb(9 14 22)" r="4" stroke="rgb(103 232 249)" strokeWidth="2" /><title>{`${row.patch}: ${percent(value)} pick rate across ${Number(row.games || 0).toLocaleString()} games`}</title></> : null}
                    </g>
                ))}
                {rows.map(({ patch }, index) => {
                    const point = pickPoints[index] || banPoints[index]
                    return <text fill="rgb(161 161 170)" fontSize="13" fontWeight="600" key={`patch-${patch}`} textAnchor="start" transform={`rotate(-35 ${point.x} ${patchLabelY})`} x={point.x} y={patchLabelY}>{patch}</text>
                })}
            </svg>
        </div>
    )
}

function PrimarySkillSummary({ rows, loading, championId, totalPicks }) {
    const visibleRows = rows.slice(0, 4)
    return (
        <div className="rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-3 py-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">First spell maxed</div>
            {loading ? <div className="h-20 animate-pulse rounded-md bg-zinc-950/70" /> : visibleRows.length ? (
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                    {visibleRows.map((row) => (
                        <div className="flex min-w-0 items-center gap-2" key={row.skill_slot}>
                            <SkillIcon championId={championId} skill={row.skill_slot} />
                            <div className="min-w-0 text-[10px] leading-tight tabular-nums">
                                <div className="font-semibold uppercase text-zinc-300">{row.skill_slot} max</div>
                                <div><PickWinRates pickRate={rowPickRate(row, totalPicks)} winRate={row.win_rate} /></div>
                                <div className="whitespace-nowrap text-zinc-500">{compactSpellGames(row.games)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : <div className="py-2 text-xs text-zinc-600">No observations.</div>}
        </div>
    )
}

function SideSummary({ rows, loading }) {
    return (
        <div className="rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-3 py-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Side Performance</div>
            <div className="grid grid-cols-2 gap-2">
                {[100, 200].map((side) => {
                    const row = rows.find((candidate) => candidate.side === side)
                    const isBlue = side === 100
                    return (
                        <div key={side}>
                            <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-zinc-500">
                                <img alt="" aria-hidden="true" className={`h-4 w-4 object-contain ${isBlue ? '-rotate-90' : 'rotate-90'}`} src={`${process.env.PUBLIC_URL}/assets/role_icons/position-top-light.svg`} />
                                <span>{isBlue ? 'Blue' : 'Red'}</span>
                            </div>
                            <div className="mt-1 text-sm font-semibold tabular-nums">{loading ? '-' : <RateText value={row?.win_rate} />}</div>
                            <div className="text-[10px] text-zinc-600">{loading ? '-' : `${row?.games || 0} games`}</div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function SummonerSpellSummary({ rows, loading, totalPicks }) {
    const visibleRows = rows.slice(0, 6)
    return (
        <div className="rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-3 py-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Summoner spells</div>
            {loading ? <div className="h-20 animate-pulse rounded-md bg-zinc-950/70" /> : visibleRows.length ? (
                <div className="grid grid-cols-2 justify-items-start gap-x-2 gap-y-2 sm:grid-cols-3">
                    {visibleRows.map((row, index) => (
                        <div className="flex min-w-0 items-center justify-start gap-2" key={`${row.spell_one_id}-${row.spell_two_id}-${index}`}>
                            <div className="flex items-center gap-1.5" title={`${row.spell_one_name || `Spell ${row.spell_one_id}`} + ${row.spell_two_name || `Spell ${row.spell_two_id}`}`}>
                                <SpellToken id={row.spell_one_id} name={row.spell_one_name} />
                                <SpellToken id={row.spell_two_id} name={row.spell_two_name} />
                            </div>
                            <div className="min-w-0 text-[10px] leading-tight tabular-nums text-zinc-500"><div><PickWinRates pickRate={rowPickRate(row, totalPicks)} winRate={row.win_rate} /></div><div className="whitespace-nowrap">{compactSpellGames(row.picked ?? row.games)}</div></div>
                        </div>
                    ))}
                </div>
            ) : <div className="py-2 text-xs text-zinc-600">No observations.</div>}
        </div>
    )
}

function RelatedChampionVisual({ view, row }) {
    const prefix = view === 'matchup' ? 'opponent' : 'ally'
    const championId = row[`${prefix}_champion_id`]
    const nameId = row[`${prefix}_name_id`]
    const displayName = formatChampionDisplayName(row[`${prefix}_name`] || nameId)
    return (
        <div className="flex items-center gap-2">
            <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
                <AssetImage alt={`Champion ${championId}`} className="h-full w-full scale-[1.18] object-cover" src={nameId ? assetUrl(`/champion_icons/${nameId}.png`) : undefined} />
            </span>
            <span>{displayName || `Champion #${championId}`} · {row[`${prefix}_role_id`]}</span>
        </div>
    )
}

const ROLE_ORDER = ['TOP', 'JUNGLE', 'MIDDLE', 'BOTTOM', 'UTILITY']
const ROLE_LABELS = { TOP: 'Top', JUNGLE: 'Jungle', MIDDLE: 'Mid', BOTTOM: 'Bottom', UTILITY: 'Support' }
const ROLE_ID_TO_PATH = { TOP: 'top', JUNGLE: 'jungle', MIDDLE: 'mid', BOTTOM: 'bot', UTILITY: 'support' }

function relatedChampionPath(nameId, roleId) {
    if (!nameId) return '/analytics/champions'
    const rolePath = ROLE_ID_TO_PATH[roleId]
    return `/analytics/champions/${encodeURIComponent(nameId)}${rolePath ? `/${rolePath}` : ''}`
}

function RelationshipGroups({ view, rows, loading }) {
    const prefix = view === 'matchup' ? 'opponent' : 'ally'
    const grouped = rows.reduce((groups, row) => {
        const role = row[`${prefix}_role_id`]
        groups[role] = [...(groups[role] || []), row]
        return groups
    }, {})
    const roles = ROLE_ORDER.filter((role) => grouped[role]?.length)

    if (loading) return <div className="py-8 text-sm text-zinc-500">Loading analytics...</div>
    if (!roles.length) return <div className="py-8 text-sm text-zinc-500">No observations.</div>

    return (
        <div className="space-y-2.5">
            {roles.map((role) => (
                <section key={role}>
                    <div className="mb-1 flex items-center gap-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400">{ROLE_LABELS[role] || role}</div>
                        <div className="h-px flex-1 bg-zinc-800" />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                        {grouped[role].map((row) => {
                            const championId = row[`${prefix}_champion_id`]
                            const nameId = row[`${prefix}_name_id`]
                            const displayName = formatChampionDisplayName(row[`${prefix}_name`] || nameId)
                            return (
                                <Link className="flex w-[84px] shrink-0 flex-col items-center gap-0.5 rounded-lg border border-zinc-800/80 bg-zinc-950/80 px-1.5 py-1.5 text-center transition hover:border-cyan-700/70 hover:bg-cyan-950/15" key={`${role}-${championId}`} to={relatedChampionPath(nameId, role)} title={`${displayName || `Champion #${championId}`}, ${ROLE_LABELS[role] || role}, ${row.games} games, ${percent(row.win_rate)} win rate`}>
                                    <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
                                        <AssetImage alt={`Champion ${championId}`} className="h-full w-full scale-[1.18] object-cover" src={nameId ? assetUrl(`/champion_icons/${nameId}.png`) : undefined} />
                                    </span>
                                    <div className="w-full truncate text-[11px] font-medium text-zinc-200">{displayName || `Champion #${championId}`}</div>
                                    <div className="text-[10px] tabular-nums text-zinc-400">{compactRelationshipGames(row.games)}</div>
                                    <div className="text-[11px] font-semibold tabular-nums"><RateText value={row.win_rate} /></div>
                                </Link>
                            )
                        })}
                    </div>
                </section>
            ))}
        </div>
    )
}

function ItemSlotTable({ rows, loading, lockedItems, onLock, totalPicks }) {
    return (
        <div className="space-y-2">
            {Array.from({ length: 6 }, (_, index) => index + 1).map((slot) => {
                const slotRows = rows.filter((row) => row.slot === slot)
                return (
                    <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/80 px-3 py-2" key={slot}>
                        <div className="mb-1.5 flex items-center justify-between gap-3">
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Item {slot}</div>
                            {lockedItems.some((item) => item.slot === slot) ? <button className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-400 hover:text-cyan-200" onClick={() => onLock(slot, null)} type="button">Unlock</button> : null}
                        </div>
                        {loading ? <div className="h-10 animate-pulse rounded-md bg-zinc-900/80" /> : slotRows.length ? (
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {slotRows.map((row) => {
                                    const active = lockedItems.some((item) => item.slot === slot && item.itemId === row.item_id)
                                    return (
                                        <button
                                            className={`flex min-w-[72px] shrink-0 flex-col items-center gap-1 rounded-md border px-1.5 py-1.5 text-center transition ${active ? 'border-cyan-500/70 bg-cyan-950/40' : 'border-zinc-800 bg-zinc-900/70 hover:border-zinc-600 hover:bg-zinc-800/80'}`}
                                            key={`${slot}-${row.item_id}`}
                                            onClick={() => onLock(slot, active ? null : row.item_id)}
                                            type="button"
                                            title={row.item_name || `Item ${row.item_id}`}
                                        >
                                            <AssetImage alt={row.item_name || `Item ${row.item_id}`} className="h-9 w-9 rounded-md border border-zinc-700 bg-zinc-900 object-cover" src={assetUrl(`/item_icons/${row.item_id}.png`)} />
                                            <span className="text-[10px] font-semibold"><PickWinRates pickRate={rowPickRate(row, totalPicks)} winRate={row.win_rate} /></span>
                                            <span className="text-[10px] font-medium tabular-nums text-zinc-300">{compactGames(row.picked)}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        ) : <div className="py-2 text-xs text-zinc-600">No observations.</div>}
                    </div>
                )
            })}
        </div>
    )
}

function EvolutionSlotTable({ rows, loading, lockedEvolutions, onLock, championId, totalPicks }) {
    return (
        <div className="space-y-1.5">
            {Array.from({ length: 4 }, (_, index) => index + 1).map((slot) => {
                const slotRows = rows.filter((row) => row.slot === slot)
                return (
                    <div className="rounded-md border border-zinc-800/80 bg-zinc-950/80 px-2.5 py-1.5" key={slot}>
                        <div className="mb-1 flex items-center justify-between gap-3">
                            <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-zinc-500">Evolution {slot}</div>
                            {lockedEvolutions.some((evolution) => evolution.slot === slot) ? <button className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-400 hover:text-cyan-200" onClick={() => onLock(slot, null)} type="button">Unlock</button> : null}
                        </div>
                        {loading ? <div className="h-10 animate-pulse rounded-md bg-zinc-900/80" /> : slotRows.length ? (
                            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                                {slotRows.map((row) => {
                                    const active = lockedEvolutions.some((evolution) => evolution.slot === slot && evolution.evolutionSlot === row.evolution_slot)
                                    return (
                                        <button
                                            className={`flex min-w-[66px] shrink-0 flex-col items-center gap-0.5 rounded-md border px-1 py-1 text-center transition ${active ? 'border-cyan-500/70 bg-cyan-950/40' : 'border-zinc-800 bg-zinc-900/70 hover:border-zinc-600 hover:bg-zinc-800/80'}`}
                                            key={`${slot}-${row.evolution_slot}`}
                                            onClick={() => onLock(slot, active ? null : row.evolution_slot)}
                                            type="button"
                                            title={`${row.evolution_slot.toUpperCase()} evolution ${slot}`}
                                        >
                                            <SkillIcon championId={championId} skill={row.evolution_slot} />
                                            <span className="text-[10px] font-semibold uppercase text-zinc-300">{row.evolution_slot}</span>
                                            <span className="text-[10px] font-semibold"><PickWinRates pickRate={rowPickRate(row, totalPicks)} winRate={row.win_rate} /></span>
                                            <span className="text-[10px] font-medium tabular-nums text-zinc-300">{compactGames(row.games)}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        ) : <div className="py-2 text-xs text-zinc-600">No observations.</div>}
                    </div>
                )
            })}
        </div>
    )
}

function StatResult({ view, row, totalPicks }) {
    if (view === 'lane') {
        return (
            <>
                <span>{row.games} games</span>
                <span>Gold {signed(row.gold_diff_average ?? row.gold_diff_10_average)}</span>
                <span>CS {signed(row.cs_diff_average ?? row.cs_diff_10_average)}</span>
                <span>Level {signed(row.level_diff_average ?? row.level_diff_10_average)}</span>
            </>
        )
    }

    const sample = row.picked ?? row.games ?? 0
    return <><span>{sample} games</span><span className="font-semibold"><PickWinRates pickRate={rowPickRate(row, totalPicks)} winRate={row.win_rate} /></span></>
}

function championSpellAssetId(championId) {
    return championId === 14101 || championId === 14102 ? 141 : championId
}

function masteryChampionIdentity(metadata, fallbackNameId) {
    if (metadata?.champion_id === 14101 || metadata?.champion_id === 14102) {
        return { name: 'Kayn', nameId: 'Kayn' }
    }
    return {
        name: metadata?.name || fallbackNameId,
        nameId: metadata?.name_id || fallbackNameId,
    }
}

function SkillIcon({ championId, skill }) {
    const slot = String(skill || '').toLowerCase()
    return <ChampionAbilityIconWithFallBack alt={`${slot.toUpperCase()} ability`} height="28" src={assetUrl(`/champion_spell_icons/${championSpellAssetId(championId)}/${slot}.png`)} width="28" />
}

function ContextLabel({ view, row, runeAssets, championId }) {
    if (view === 'side') return row.side === 100 ? 'BLUE SIDE' : 'RED SIDE'
    if (view === 'duration') return row.duration_bucket
    if (view === 'matchup' || view === 'synergy') return <RelatedChampionVisual view={view} row={row} />
    if (view === 'starting-items') {
        return <div className="flex flex-wrap items-center gap-2"><ItemToken id={row.item_one_id} name={row.item_one_name} quantity={row.item_one_quantity} /><ItemToken id={row.item_two_id} name={row.item_two_name} quantity={row.item_two_quantity} /></div>
    }
    if (view === 'rune-pages') return <RunePageVisual row={row} runeAssets={runeAssets} />
    if (view === 'boots') return <ItemToken id={row.completed_boot_id} name={row.completed_boot_name} />
    if (view === 'spells') {
        return <div className="flex items-center gap-2"><SpellToken id={row.spell_one_id} name={row.spell_one_name} /><SpellToken id={row.spell_two_id} name={row.spell_two_name} /></div>
    }
    if (view === 'first-three-skills') {
        return <div className="flex items-center gap-2">{[row.skill_one, row.skill_two, row.skill_three].filter(Boolean).map((skill, index) => <span className="flex items-center gap-1.5" key={`${skill}-${index}`}><SkillIcon championId={championId} skill={skill} /><span className="text-xs uppercase text-zinc-300">{skill}</span></span>)}</div>
    }
    if (view === 'primary-skill') return <div className="flex items-center gap-2"><SkillIcon championId={championId} skill={row.skill_slot} /><span>{String(row.skill_slot || '').toUpperCase()} MAX</span></div>
    return '-'
}

function AnalyticsTable({ view, rows, loading, runeAssets, lockedItems, onLock, lockedEvolutions, onLockEvolution, championId, totalPicks }) {
    const label = VIEW_OPTIONS.find((option) => option.value === view)?.label
    const isRelationshipView = view === 'matchup' || view === 'synergy'
    const isHighlightView = view === 'evolution-paths' && rows.length > 0
    if (view === 'evolution-paths' && !loading && !rows.length) return null
    return (
        <section className={`rounded-xl border border-zinc-800 bg-zinc-950/75 ${view === 'evolution-paths' ? 'p-2.5' : 'p-3'}`}>
            {isRelationshipView ? (
                <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{view === 'matchup' ? 'Matchups' : 'Synergy'}</div>
                    <div className="flex items-center overflow-hidden rounded-md border border-zinc-700/70 bg-gradient-to-r from-cyan-950/80 via-zinc-700/80 to-amber-950/60 text-[9px] font-bold uppercase tracking-[0.14em]">
                        <span className="px-2 py-1 text-cyan-300">Good {view === 'matchup' ? 'matchups' : 'synergy'}</span>
                        <span className="h-3.5 w-px bg-zinc-600/70" />
                        <span className="px-2 py-1 text-amber-200">Bad {view === 'matchup' ? 'matchups' : 'synergy'}</span>
                    </div>
                </div>
            ) : (
                <div className={`mb-2 flex items-center justify-between gap-3 ${isHighlightView ? 'rounded-md bg-cyan-950/20 px-2 py-1.5' : ''}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${isHighlightView ? 'text-cyan-200' : 'text-zinc-500'}`}>{label}</div>
                    {isHighlightView ? <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-cyan-400/80">Unique insight</div> : null}
                </div>
            )}
            {loading && view !== 'item-builds' && view !== 'evolution-paths' ? <div className="py-6 text-sm text-zinc-500">Loading analytics...</div> : null}
            {!loading && view !== 'item-builds' && view !== 'evolution-paths' && !rows.length ? <div className="py-6 text-sm text-zinc-500">No observations.</div> : null}
            {view === 'item-builds' ? <ItemSlotTable rows={rows} loading={loading} lockedItems={lockedItems} onLock={onLock} totalPicks={totalPicks} /> : null}
            {view === 'evolution-paths' ? <EvolutionSlotTable rows={rows} loading={loading} lockedEvolutions={lockedEvolutions} onLock={onLockEvolution} championId={championId} totalPicks={totalPicks} /> : null}
            {view === 'duration' && !loading && rows.length ? <DurationChart rows={rows} /> : null}
            {view === 'matchup' || view === 'synergy' ? <RelationshipGroups view={view} rows={rows} loading={loading} /> : null}
            {view !== 'item-builds' && view !== 'evolution-paths' && view !== 'duration' && view !== 'matchup' && view !== 'synergy' && !loading && rows.length ? (
                <div className="space-y-1.5">
                    {rows.map((row, index) => (
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-lg border border-zinc-800/80 bg-zinc-950/80 px-3 py-2" key={`${view}-${index}-${JSON.stringify(row)}`}>
                            <div className="min-w-0 text-sm font-medium text-zinc-200"><ContextLabel view={view} row={row} runeAssets={runeAssets} championId={championId} /></div>
                            <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 text-right text-[11px] tabular-nums text-zinc-400"><StatResult view={view} row={row} totalPicks={totalPicks} /></div>
                        </div>
                    ))}
                </div>
            ) : null}
        </section>
    )
}

export default function ChampionAnalytics() {
    const deploymentCapabilities = useDeploymentCapabilities()
    const scopedOptions = scopedPlatformOptions(PLATFORM_OPTIONS, deploymentCapabilities)
    const { championNameId, roleId: rolePath, '*': scopePath = '' } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const routeScope = parseAnalyticsScope(scopePath)
    const initialMasteryBucket = masteryBucketFromUrl(searchParams.get('mastery'))
    const [metadata, setMetadata] = useState(null)
    const [performance, setPerformance] = useState(null)
    const [roleDistribution, setRoleDistribution] = useState([])
    const [globalTrends, setGlobalTrends] = useState([])
    const [jungleItemCompletion, setJungleItemCompletion] = useState(null)
    const [supportItemCompletion, setSupportItemCompletion] = useState(null)
    const [transformations, setTransformations] = useState([])
    const [keystoneStats, setKeystoneStats] = useState([])
    const [rowsByView, setRowsByView] = useState({})
    const [platform, setPlatform] = useState(() => routeScope.platform || searchParams.get('region') || '')
    const [roleId, setRoleId] = useState(() => searchParams.get('role') || roleIdFromPath(rolePath))
    const [sliceMode, setSliceMode] = useState(() => initialMasteryBucket ? 'mastery' : 'tier')
    const [tier, setTier] = useState(() => initialMasteryBucket ? '' : (routeScope.tier || searchParams.get('tier') || 'Emerald+'))
    const [masteryBucket, setMasteryBucket] = useState(() => initialMasteryBucket)
    const [patch, setPatch] = useState(() => searchParams.get('patch') || '')
    const [patchOptions, setPatchOptions] = useState([])
    const [patchOptionsLoaded, setPatchOptionsLoaded] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [runeAssets, setRuneAssets] = useState({})
    const [lockedItems, setLockedItems] = useState(() => parseLockedItems(searchParams.get('items')))
    const [lockedEvolutions, setLockedEvolutions] = useState(() => parseLockedEvolutions(searchParams.get('evolutions')))
    const [selectedKeystoneId, setSelectedKeystoneId] = useState(() => searchParams.get('keystone') || '')
    // Analytics uses the game's two-digit season year (for example, 26 for 2026).
    const year = new Date().getUTCFullYear() % 100
    const tierOptions = TIER_OPTIONS
    const masteryOptions = MASTERY_OPTIONS.slice(1).map((option) => ({ value: option, label: MASTERY_LABELS[option] }))

    useEffect(() => {
        const pathRoleId = roleIdFromPath(rolePath)
        if (pathRoleId && pathRoleId !== roleId) setRoleId(pathRoleId)
    }, [roleId, rolePath])

    useEffect(() => {
        if (!scopePath) return
        setPlatform(routeScope.platform)
        setTier(routeScope.tier || 'Emerald+')
        setMasteryBucket('')
        setSliceMode('tier')
    }, [routeScope.platform, routeScope.tier, scopePath])

    function handleSliceModeChange(nextMode) {
        setSliceMode(nextMode)
        if (nextMode === 'tier') {
            setMasteryBucket('')
            setTier(tier || 'Emerald+')
        } else {
            setTier('')
            setMasteryBucket(masteryBucket || MASTERY_OPTIONS[MASTERY_OPTIONS.length - 1])
        }
    }

    useEffect(() => {
        if (Object.keys(runeAssets).length) return undefined

        let cancelled = false
        async function loadRuneAssets() {
            const trees = [8000, 8100, 8200, 8300, 8400]
            const requests = trees.flatMap((tree) => [1, 2, 3, 4].map(async (slot) => {
                const group = slot === 1 ? 'major' : 'minor'
                const response = await axiosForContent.get(`/layouts/runes/${group}/${tree}/slot_${slot}/layout.json`)
                return { tree, slot, names: response.data, kind: 'tree' }
            }))
            requests.push(...[1, 2, 3].map(async (slot) => {
                const response = await axiosForContent.get(`/layouts/runes/stat-shards/slot_${slot}/layout.json`)
                return { slot, names: response.data, kind: 'stat' }
            }))
            const results = await Promise.allSettled(requests)
            const nextAssets = {}
            results.forEach((result) => {
                if (result.status !== 'fulfilled' || !Array.isArray(result.value.names)) return
                result.value.names.forEach((name) => {
                    const fileName = String(name)
                    const id = fileName.replace(/\.png$/, '')
                    const url = result.value.kind === 'stat'
                        ? assetUrl(`/major_and_minor_rune_icons/stat_mods/slot_${result.value.slot}/${fileName}`)
                        : assetUrl(`/major_and_minor_rune_icons/trees/${result.value.tree}/slot_${result.value.slot}/${fileName}`)
                    nextAssets[id] = url
                })
            })
            if (!cancelled) setRuneAssets(nextAssets)
        }

        loadRuneAssets()
        return () => { cancelled = true }
    }, [runeAssets])

    useEffect(() => {
        const controller = new AbortController()
        async function loadPatchOptions() {
            try {
                const response = await axiosForAPI.get('/analytics/patch-options/', {
                    params: { year },
                    signal: controller.signal,
                })
                const options = (response.data?.patches || []).map((option) => ({
                    ...option,
                    label: `PATCH ${option.value}`,
                }))
                setPatchOptions(options)
                setPatch((current) => options.some((option) => option.value === current) ? current : (options[0]?.value || ''))
            } catch (requestError) {
                if (!axios.isCancel(requestError) && requestError?.code !== 'ERR_CANCELED') setPatchOptions([])
            } finally {
                if (!controller.signal.aborted) setPatchOptionsLoaded(true)
            }
        }
        loadPatchOptions()
        return () => controller.abort()
    }, [year])

    useEffect(() => {
        const next = new URLSearchParams()
        if (platform && !routeScope.platform) next.set('region', platform)
        if (roleId && !rolePath) next.set('role', roleId)
        if (tier && !routeScope.tier) next.set('tier', tier)
        if (masteryBucket) next.set('mastery', masteryUrlValue(masteryBucket))
        if (patch) next.set('patch', patch)
        if (selectedKeystoneId) next.set('keystone', selectedKeystoneId)
        if (lockedItems.length) next.set('items', lockedItems.map((item) => `${item.slot}:${item.itemId}`).join(','))
        if (lockedEvolutions.length) next.set('evolutions', lockedEvolutions.map((evolution) => `${evolution.slot}:${evolution.evolutionSlot}`).join(','))
        setSearchParams(next, { replace: true })
    }, [lockedEvolutions, lockedItems, masteryBucket, patch, platform, roleId, rolePath, routeScope.platform, routeScope.tier, selectedKeystoneId, setSearchParams, tier])

    useEffect(() => {
        const controller = new AbortController()

        async function loadAnalytics() {
            if (!patchOptionsLoaded) return
            if (!patch) {
                setError('Select a patch to load this report.')
                setLoading(false)
                return
            }
            try {
                setLoading(true)
                setError('')
                const championResponse = await axiosForAPI.get('/analytics/champion-metadata/', {
                    params: { nameId: championNameId },
                    signal: controller.signal,
                })
                const champion = championResponse.data
                setMetadata(champion)

                const roleParams = {
                    year,
                    platform: platform || undefined,
                    tier: tier || undefined,
                    masteryBucket: masteryBucket || undefined,
                    patch: patch || undefined,
                    championId: champion.champion_id,
                }
                const roleResponse = await axiosForAPI.get('/analytics/champion-role-distribution/', {
                    params: roleParams,
                    signal: controller.signal,
                })
                setRoleDistribution(roleResponse.data?.roles || [])

                if (!roleId) {
                    const defaultRole = roleResponse.data?.default_role_id
                    if (!defaultRole) {
                        setError('No observations.')
                    } else {
                        setRoleId(defaultRole)
                    }
                    return
                }

                const baseParams = {
                    year,
                    platform: platform || undefined,
                    roleId,
                    tier: tier || undefined,
                    masteryBucket: masteryBucket || undefined,
                    patch: patch || undefined,
                    championId: champion.champion_id,
                }
                const requests = Object.fromEntries(VIEW_OPTIONS.map((option) => {
                    const params = { ...baseParams, masteryBucket: masteryBucket || undefined }
                    if (option.value === 'item-builds') {
                        params.lockedItems = lockedItems.length ? lockedItems.map((item) => `${item.slot}:${item.itemId}`).join(',') : undefined
                        return [option.value, axiosForAPI.get('/analytics/champion-item-builds/', { params, signal: controller.signal })]
                    }
                    if (option.value === 'evolution-paths') {
                        params.lockedEvolutions = lockedEvolutions.length ? lockedEvolutions.map((evolution) => `${evolution.slot}:${evolution.evolutionSlot}`).join(',') : undefined
                        return [option.value, axiosForAPI.get('/analytics/champion-evolution-paths/', { params, signal: controller.signal })]
                    }
                    if (option.value === 'rune-pages') {
                        params.keystoneId = selectedKeystoneId || undefined
                        return [option.value, axiosForAPI.get('/analytics/champion-runes/', { params, signal: controller.signal })]
                    }
                    return [option.value, axiosForAPI.get('/analytics/champion-context/', { params: { ...params, view: option.value, limit: option.value === 'matchup' || option.value === 'synergy' ? undefined : 30 }, signal: controller.signal })]
                }))
                const [performanceResponse, trendResponse, jungleResponse, supportCompletionResponse, transformationResponse, keystoneResponse, ...reportResponses] = await Promise.all([
                    axiosForAPI.get('/analytics/champion-performance/', { params: baseParams, signal: controller.signal }),
                    axiosForAPI.get('/analytics/champion-global-trends/', { params: { year, championId: champion.champion_id }, signal: controller.signal }),
                    roleId === 'JUNGLE'
                        ? axiosForAPI.get('/analytics/champion-jungle-item-completion/', { params: baseParams, signal: controller.signal })
                        : Promise.resolve({ data: null }),
                    roleId === 'UTILITY'
                        ? axiosForAPI.get('/analytics/champion-support-item-completion/', { params: baseParams, signal: controller.signal })
                        : Promise.resolve({ data: null }),
                    axiosForAPI.get('/analytics/champion-transformations/', { params: baseParams, signal: controller.signal }),
                    axiosForAPI.get('/analytics/champion-keystones/', { params: baseParams, signal: controller.signal }),
                    ...Object.values(requests),
                ])
                setPerformance(performanceResponse.data)
                setGlobalTrends(trendResponse.data?.results || [])
                setJungleItemCompletion(jungleResponse.data || null)
                setSupportItemCompletion(supportCompletionResponse.data || null)
                setTransformations(transformationResponse.data?.results || [])
                setKeystoneStats(keystoneResponse.data?.results || [])
                setRowsByView(Object.fromEntries(VIEW_OPTIONS.map((option, index) => [option.value, reportResponses[index].data?.results || []])))
            } catch (requestError) {
                if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') return
                setError(requestError?.response?.data?.detail || 'Unable to load champion analytics.')
                setRowsByView({})
                setRoleDistribution([])
                setGlobalTrends([])
                setJungleItemCompletion(null)
                setSupportItemCompletion(null)
                setTransformations([])
                setKeystoneStats([])
            } finally {
                if (!controller.signal.aborted) setLoading(false)
            }
        }

        loadAnalytics()
        return () => controller.abort()
    }, [championNameId, lockedEvolutions, lockedItems, masteryBucket, patch, patchOptionsLoaded, platform, roleId, selectedKeystoneId, tier, year])

    function lockItem(slot, itemId) {
        setLockedItems((current) => {
            const withoutSlot = current.filter((item) => item.slot !== slot)
            return itemId === null ? withoutSlot : [...withoutSlot, { slot, itemId }].sort((a, b) => a.slot - b.slot)
        })
    }

    function lockEvolution(slot, evolutionSlot) {
        setLockedEvolutions((current) => {
            const withoutSlot = current.filter((evolution) => evolution.slot !== slot)
            return evolutionSlot === null
                ? withoutSlot
                : [...withoutSlot, { slot, evolutionSlot }].sort((left, right) => left.slot - right.slot)
        })
    }

    const selectedRole = ROLE_OPTIONS.find((option) => option.value === roleId)
    const masteryChampion = masteryChampionIdentity(metadata, championNameId)
    const isNoObservationState = error?.trim() === NO_OBSERVATIONS_MESSAGE
    const selectedRoleStats = roleDistribution.find((row) => row.role_id === roleId)
    const selectedTier = TIER_OPTIONS.find((option) => option.value === tier)
    const roleOptions = roleId ? ROLE_OPTIONS : [{ value: '', label: 'SELECTING ROLE...' }, ...ROLE_OPTIONS]
    const seoRoleLabels = {
        TOP: 'Top',
        JUNGLE: 'Jungle',
        MIDDLE: 'Mid',
        BOTTOM: 'Bot',
        UTILITY: 'Support',
    }
    const seoRoleDisplayLabels = {
        TOP: 'Top Lane',
        JUNGLE: 'Jungle',
        MIDDLE: 'Mid Lane',
        BOTTOM: 'Bot Lane',
        UTILITY: 'Support',
    }
    const seoRoleLabel = seoRoleLabels[roleId]
    const seoRoleDisplayLabel = seoRoleDisplayLabels[roleId]
    const seoChampionName = metadata?.name || championNameId
    const seoPatchLabel = patch || 'the current patch'
    const seoPlatformLabel = platform
        ? PLATFORM_OPTIONS.find((option) => option.value === platform)?.label
        : null
    const selectedScopeTierLabel = sliceMode === 'mastery'
        ? `${MASTERY_LABELS[masteryBucket] || 'Mastery'} mastery`
        : (tier || 'Emerald+')
    const seoScopePhrase = `${selectedScopeTierLabel} players${seoPlatformLabel ? ` in ${seoPlatformLabel} (${platform.toUpperCase()})` : ''}`
    const seoTitle = seoRoleLabel
        ? `${seoChampionName} ${seoRoleDisplayLabel} Win Rates, Builds & Stats${routeScope.tier || routeScope.platform ? ` for ${seoScopePhrase}` : ''} | WINRATES.GG™`
        : `${seoChampionName} Champion Analytics | WINRATES.GG™`
    const seoFeatureDescription = 'Includes best items, best builds, best rune pages, and best keystones; good matchups and bad matchups; best teammates and best synergies; best combinations and bad combinations; champion evolution stats and best evolution order; Red Kayn vs. Blue Kayn form stats and best Kayn form analysis; first item and rush item completion timing; and jungle and support item completion times.'
    const seoDescription = seoRoleLabel
        ? `${seoChampionName} ${seoRoleDisplayLabel} win rates, best builds, itemization, and performance analysis for ${seoScopePhrase} on patch ${seoPatchLabel}. ${seoFeatureDescription}`
        : `Champion win rates, best builds, itemization, matchups, scaling profiles, and performance stats for ${seoChampionName}. ${seoFeatureDescription}`
    const canonicalPath = `/analytics/champions/${encodeURIComponent(championNameId)}${rolePath ? `/${rolePath}` : ''}${analyticsScopeSuffix(routeScope)}`

    return (
        <div className="mx-auto w-full max-w-screen-lg text-zinc-200">
            <div className="mx-4 pt-6 pb-4 custom-lg:mx-0 sm:pt-8 sm:pb-6">
            <Seo
                title={seoTitle}
                description={seoDescription}
                canonical={canonicalPath}
            />
            <div className="mb-2 flex items-center justify-between gap-3">
                <Link className="inline-flex text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 transition hover:text-cyan-300" to="/analytics/champions">Return to champions</Link>
                <Link
                    className="inline-flex text-right text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 transition hover:text-cyan-300"
                    to={`/analytics/mastery/${encodeURIComponent(masteryChampion.nameId)}`}
                >
                    {masteryChampion.name} Players
                </Link>
            </div>
            <header className="rounded-xl border border-zinc-800 bg-zinc-950/80 p-3 sm:p-4">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        {metadata?.name_id ? <span className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900"><img className="h-full w-full scale-[1.18] object-cover" src={assetUrl(`/champion_icons/${metadata.name_id}.png`)} alt={`${metadata.name} icon`} /></span> : null}
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 leading-none">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">{performance?.decided_games || 0} games</span>
                                <span aria-hidden="true" className="text-[9px] leading-none text-zinc-700">·</span>
                                <span className="text-[9px] font-medium uppercase tracking-[0.1em] leading-none text-zinc-500">{selectedScopeTierLabel}</span>
                                {seoPlatformLabel ? <><span aria-hidden="true" className="text-[9px] leading-none text-zinc-700">·</span><span className="text-[9px] font-medium uppercase tracking-[0.1em] leading-none text-zinc-500">{seoPlatformLabel} ({platform.toUpperCase()})</span></> : null}
                                <span aria-hidden="true" className="text-[9px] leading-none text-zinc-700">·</span>
                                <span className="text-[9px] font-medium uppercase tracking-[0.1em] leading-none text-zinc-500">Patch {seoPatchLabel}</span>
                            </div>
                            <h1 className="mt-0.5 truncate text-2xl font-semibold tracking-tight text-zinc-100">{seoChampionName}{seoRoleDisplayLabel ? ` ${seoRoleDisplayLabel}` : ''} Stats</h1>
                        </div>
                    </div>
                    <div className={`grid w-full min-w-0 shrink-0 grid-cols-2 divide-x divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900/70 text-center text-xs text-zinc-400 sm:w-[38.5rem] sm:max-w-full sm:divide-y-0 ${sliceMode === 'tier' ? 'sm:grid-cols-6' : 'sm:grid-cols-4'}`}>
                        <div className="min-w-0 px-3 py-2.5"><MetricLabel label="WIN RATE" description="The win rate of a champion when picked in the relevant skill band (i.e. How Lux performs in Gold) or when piloted by a player in the selected mastery band (i.e. How high mastery Riven players perform)." /><div className="mt-0.5 text-xl font-semibold tabular-nums">{performance ? <RateText value={performance.win_rate} /> : '-'}</div></div>
                        {sliceMode === 'tier' ? <>
                            <div className="min-w-0 px-3 py-2.5"><MetricLabel label="TIER WR" description="The average win rate of players in the selected tier(s). While the player base has a 50% win rate, fluid skill bands do not." /><div className="mt-0.5 text-xl font-semibold tabular-nums">{performance ? <RateText value={performance.tier_cohort_win_rate?.average_player_win_rate} /> : '-'}</div></div>
                            <div className="min-w-0 px-3 py-2.5"><MetricLabel label="WR DELTA" description="The win rate of a champion minus the win rate of the average player in the same skill band. This is an estimate for over or under performance." /><div className="mt-0.5 text-xl font-semibold tabular-nums" style={{ color: winRateDeltaColor(performance?.win_rate_delta) }}>{performance?.win_rate_delta === null || performance?.win_rate_delta === undefined ? '-' : `${signed(performance.win_rate_delta)}%`}</div></div>
                        </> : null}
                        <div className="min-w-0 px-3 py-2.5"><div className="flex min-h-[18px] items-start justify-center text-[10px] font-bold uppercase leading-tight tracking-[0.16em] text-zinc-500">Pick rate</div><div className="mt-0.5 text-xl font-semibold tabular-nums text-zinc-100">{performance ? percent(performance.pick_rate) : '-'}</div></div>
                        <div className="min-w-0 px-3 py-2.5"><div className="flex min-h-[18px] items-start justify-center text-[10px] font-bold uppercase leading-tight tracking-[0.16em] text-zinc-500">Ban rate</div><div className="mt-0.5 text-xl font-semibold tabular-nums">{performance ? <BanRateText value={performance.ban_rate} /> : '-'}</div></div>
                        <div className="min-w-0 px-3 py-2.5"><div className="flex min-h-[18px] items-start justify-center text-[10px] font-bold uppercase leading-tight tracking-[0.16em] text-zinc-500">Role rate</div><div className="mt-0.5 text-xl font-semibold tabular-nums text-zinc-100">{selectedRoleStats ? percent(selectedRoleStats.role_pick_rate) : '-'}</div></div>
                    </div>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-5">
                    <div aria-label="REPORT TYPE" className="flex h-[34px] min-w-0 overflow-hidden rounded-md border border-zinc-700 bg-zinc-900/90 p-0.5" role="group">
                        {['tier', 'mastery'].map((mode) => (
                            <button
                                aria-pressed={sliceMode === mode}
                                className={`h-full min-w-0 flex-1 rounded-[3px] px-2 text-[10px] font-bold uppercase tracking-[0.1em] transition-colors ${sliceMode === mode ? 'bg-cyan-950/70 text-cyan-200 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.45)]' : 'text-zinc-500 hover:bg-zinc-800/80 hover:text-zinc-300'}`}
                                key={mode}
                                onClick={() => handleSliceModeChange(mode)}
                                type="button"
                            >
                                {mode === 'tier' ? 'TIER' : 'MASTERY'}
                            </button>
                        ))}
                    </div>
                    {sliceMode === 'tier' ? (
                        <FilterSelect ariaLabel="Tier" icon={selectedTier?.icon} options={tierOptions} value={tier} onChange={(event) => {
                            setSliceMode('tier')
                            setTier(event.target.value)
                            setMasteryBucket('')
                        }} />
                    ) : (
                        <FilterSelect ariaLabel="Mastery" options={masteryOptions} value={masteryBucket} onChange={(event) => {
                            setSliceMode('mastery')
                            setMasteryBucket(event.target.value)
                            setTier('')
                        }} />
                    )}
                    <FilterSelect ariaLabel="Patch" options={patchOptions.length ? patchOptions : [{ value: '', label: 'CURRENT PATCH' }]} value={patch} onChange={(event) => setPatch(event.target.value)} />
                    <FilterSelect ariaLabel="Role" icon={selectedRole?.icon} options={roleOptions} value={roleId} onChange={(event) => setRoleId(event.target.value)} />
                    <FilterSelect ariaLabel="Region" options={scopedOptions} value={platform} onChange={(event) => setPlatform(event.target.value)} />
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[1.25fr_0.7fr_1.8fr]">
                        <PrimarySkillSummary rows={rowsByView['primary-skill'] || []} loading={loading} championId={metadata?.champion_id} totalPicks={performance?.picked} />
                        <SideSummary rows={rowsByView.side || []} loading={loading} />
                        <SummonerSpellSummary rows={rowsByView.spells || []} loading={loading} totalPicks={performance?.picked} />
                </div>
            </header>
            {error ? <div className={`mt-3 rounded-lg border px-4 py-3 text-sm ${isNoObservationState ? 'border-amber-400/50 bg-amber-300/10 text-amber-100' : 'border-rose-800/60 bg-rose-950/30 text-rose-100'}`}>{error}</div> : null}
            <div className="mt-4 space-y-3">
                <ChampionPerformanceSummary performance={performance} roleId={roleId} championId={metadata?.champion_id} jungleItemCompletion={jungleItemCompletion} supportItemCompletion={supportItemCompletion} transformations={transformations} loading={loading} />
                <LaneProfileSummary rows={rowsByView.lane || []} loading={loading} />
                {globalTrends.length || rowsByView.duration?.length ? (
                    <section className="grid items-start gap-3 md:grid-cols-2">
                        {rowsByView.duration?.length ? <DurationChart rows={rowsByView.duration} /> : null}
                        {globalTrends.length ? <RateTrendChart rows={globalTrends} /> : null}
                    </section>
                ) : null}
                        <AnalyticsTable view="first-three-skills" rows={rowsByView['first-three-skills'] || []} loading={loading} runeAssets={runeAssets} lockedItems={lockedItems} onLock={lockItem} lockedEvolutions={lockedEvolutions} onLockEvolution={lockEvolution} championId={metadata?.champion_id} totalPicks={performance?.picked} />
                        {rowsByView['evolution-paths']?.length ? <AnalyticsTable view="evolution-paths" rows={rowsByView['evolution-paths']} loading={loading} runeAssets={runeAssets} lockedItems={lockedItems} onLock={lockItem} lockedEvolutions={lockedEvolutions} onLockEvolution={lockEvolution} championId={metadata?.champion_id} totalPicks={performance?.picked} /> : null}
                        <AnalyticsTable view="starting-items" rows={rowsByView['starting-items'] || []} loading={loading} runeAssets={runeAssets} lockedItems={lockedItems} onLock={lockItem} lockedEvolutions={lockedEvolutions} onLockEvolution={lockEvolution} championId={metadata?.champion_id} totalPicks={performance?.picked} />
                {REPORT_OPTIONS.map((option) => (
                    <div key={option.value}>
                        {option.value === 'rune-pages' ? (
                            <KeystonePerformance
                                rows={keystoneStats}
                                loading={loading}
                                runeAssets={runeAssets}
                                selectedKeystoneId={selectedKeystoneId}
                                onSelect={setSelectedKeystoneId}
                                totalPicks={performance?.picked}
                            />
                        ) : null}
                        <AnalyticsTable view={option.value} rows={rowsByView[option.value] || []} loading={loading} runeAssets={runeAssets} lockedItems={lockedItems} onLock={lockItem} lockedEvolutions={lockedEvolutions} onLockEvolution={lockEvolution} championId={metadata?.champion_id} totalPicks={performance?.picked} />
                    </div>
                ))}
            </div>
            </div>
        </div>
    )
}
